import type { ProductComingItem, StockRow } from '@econtractor/types';
import * as apiWarehouse from 'api/apiWarehouses';
import vconst from 'utils/helpers/structs/vconst';

interface PriceStockRow {
    stock?: number;
    operPrice?: number;
    operPriceSale?: number;
    operPriceRate?: number;
    operPriceSaleRate?: number;
}

const pushManagerTransferRows = (
    productId: number,
    productName: string,
    vendorName: string,
    article: string,
    count: number,
    stockRows: PriceStockRow[],
): ProductComingItem[] | null => {
    let remain = count;
    const rows: ProductComingItem[] = [];

    for (const stockRow of stockRows) {
        if (remain <= 0) break;
        const lotCount = Number(stockRow?.stock || 0);
        if (lotCount <= 0) continue;

        const cCount = Math.min(remain, lotCount);
        remain -= cCount;

        rows.push({
            setId: 0,
            product: {
                ID: productId,
                value: productId,
                label: productName,
                vendor: vendorName || '',
                article: article || '',
            },
            coming: {
                cCount,
                cDescription: '',
                price: Number(stockRow?.operPrice || 0),
                priceSale: Number(stockRow?.operPriceSale || 0),
                priceRate: Number(stockRow?.operPriceRate || 0),
                priceSaleRate: Number(stockRow?.operPriceSaleRate || 0),
            },
        });
    }

    return remain > 0 ? null : rows;
};

export const writeOffDeletedStock = async (
    wId: number,
    stock: StockRow,
    splitByPrice: boolean,
): Promise<{ stat: boolean; errCode?: string | null }> => {
    const productId = Number(stock.productId || 0);
    const stockCount = Number(stock.stockCount || 0);
    if (!productId || stockCount <= 0) {
        return { stat: false, errCode: 'NO_PRODUCT_STOCK' };
    }

    const product = {
        ID: productId,
        value: productId,
        label: stock.prodName || `#${productId}`,
        vendor: stock.vendorName || '',
        article: stock.prodArticle || '',
    };

    let transferRows: ProductComingItem[];

    if (splitByPrice) {
        const stockRows = ((await apiWarehouse.getStockByProductId(wId, productId)) || []) as PriceStockRow[];
        const totalByPrice = stockRows.reduce((sum, row) => sum + Number(row?.stock || 0), 0);
        if (totalByPrice <= 0) return { stat: false, errCode: 'NO_PRODUCT_STOCK' };
        const managerRows = pushManagerTransferRows(
            productId,
            product.label,
            product.vendor,
            product.article,
            totalByPrice,
            stockRows,
        );
        if (!managerRows?.length) return { stat: false, errCode: 'NO_PRODUCT_STOCK' };
        transferRows = managerRows;
    } else {
        transferRows = [
            {
                setId: 0,
                product,
                coming: {
                    cCount: stockCount,
                    cDescription: '',
                    price: 0,
                    priceSale: 0,
                    priceRate: 0,
                    priceSaleRate: 0,
                },
            },
        ];
    }

    const resTransfer = await apiWarehouse.transferBetweenWarehouses(
        wId,
        vconst.waherouseWriteOffId,
        transferRows,
    );

    if (!resTransfer || resTransfer === false) return { stat: false };
    return {
        stat: Boolean(resTransfer.stat),
        errCode: resTransfer.errCode || null,
    };
};
